import { useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import {
  ContactDirectoriesAPI,
  ContactDirectoriesQuery,
  ContactDirectoriesQueryKeys,
} from '@frontend/api-contact-directories';
import { Trans, useTranslation } from '@frontend/i18n';
import { usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useMutation } from '@frontend/react-query-helpers';
import { theme } from '@frontend/theme';
import { Modal, ModalControlModalProps, Table, Text, useAlert } from '@frontend/design-system';
import { logError } from '../../../../utils/loggers';
import type { SelectedDeviceToEditType } from '../tables/device-assignments-table';
import { CommonModalWrapper } from './modal-wrapper';

type Props = ModalControlModalProps & {
  selectedDeviceToEdit?: SelectedDeviceToEditType;
};

export const AssignContactDirectriesToDevices = ({ selectedDeviceToEdit, ...modalProps }: Props) => {
  const { t } = useTranslation('phone', { keyPrefix: 'contact-directories' });
  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');
  const tenantId = settingsTenantLocation?.phoneTenantId;
  const [selectedDirectoryIds, setSelectedDirectoryIds] = useState<string[]>(
    selectedDeviceToEdit?.contactListsIds ?? []
  );
  const queryClient = useQueryClient();
  const alert = useAlert();

  const { useGetContactDirectories } = ContactDirectoriesQuery.useContactDirectoryQueries({ tenantId });
  const { data: { contactLists } = { contactLists: [] } } = useGetContactDirectories();

  const assignedDirectories: Record<string, boolean> = {};
  selectedDeviceToEdit?.contactListsIds?.forEach((item) => {
    assignedDirectories[item] = true;
  });

  const assignedDirectoriesIds = useMemo(() => {
    return Object.keys(assignedDirectories);
  }, [assignedDirectories]);

  const { mutate: assignDevice } = useMutation(ContactDirectoriesAPI.assignDevicetoContactLists, {
    onSuccess: () => {
      modalProps.onClose();
      queryClient.invalidateQueries([
        tenantId,
        ...ContactDirectoriesQueryKeys.queryKeys.getContactDirectoriesByDeviceIds(
          selectedDeviceToEdit?.assignableDeviceIds ?? []
        ),
      ]);
      queryClient.invalidateQueries([tenantId, ...ContactDirectoriesQueryKeys.queryKeys.getContactDirectories()]);
    },
    onError: (err) => {
      alert.error(t('Failed to assign devices to contact list'));
      logError('AssignContactDirectriesToDevices:assignDeviceMutation | Failed to assign devices to contact list', {
        data: { tenantId, deviceId: selectedDeviceToEdit?.deviceId, error: err },
      });
      throw err;
    },
  });

  const handleSave = () => {
    if (selectedDeviceToEdit?.deviceId) {
      assignDevice({
        contactListIds: selectedDirectoryIds,
        deviceId: selectedDeviceToEdit.deviceId,
      });
    }
  };

  const compareAssignedIds = (): boolean => {
    const notYetAssigned = [];
    if (selectedDirectoryIds.length !== assignedDirectoriesIds.length) return false;
    selectedDirectoryIds.map((selectedId) => {
      if (!assignedDirectoriesIds.includes(selectedId)) {
        notYetAssigned.push(selectedId);
      }
    });
    return !notYetAssigned.length;
  };

  return (
    <CommonModalWrapper disableCloseOnEscape disableCloseOnOverlayClick {...modalProps}>
      <Modal.Header onClose={modalProps.onClose}>{t('Assign Contact Lists')}</Modal.Header>
      <Modal.Body>
        <Text css={{ margin: theme.spacing(1, 0, 3) }}>
          <Trans t={t}>
            Select up to 5 contact lists to assign to <b>{selectedDeviceToEdit?.name}</b>{' '}
          </Trans>
        </Text>
        <Table
          data={contactLists ?? []}
          uniqueRowId={({ id }) => id}
          colConfig={[
            {
              Header: t('Contact List Name'),
              id: 'contact-list-name',
              accessor: ({ name }) => name,
              disableSortBy: true,
            },
          ]}
          isSelectable
          rowSelectionConfig={{
            hideBulkSelection: true,
            initialState: assignedDirectories,
            isRowDisabled: ({ id }) => {
              if (!id) return true;
              if (assignedDirectoriesIds.includes(id) || selectedDirectoryIds.includes(id)) {
                return false;
              } else if (assignedDirectoriesIds.length === 5 || selectedDirectoryIds.length === 5) {
                return true;
              }
              return false;
            },
            onSelectionToggle: (_, __, ids) => {
              setSelectedDirectoryIds(Object.keys(ids));
            },
          }}
        />
      </Modal.Body>
      <Modal.Actions
        primaryLabel={t('Save')}
        secondaryLabel={t('Cancel')}
        onSecondaryClick={modalProps.onClose}
        onPrimaryClick={handleSave}
        disablePrimary={compareAssignedIds()}
      />
    </CommonModalWrapper>
  );
};
